import { useGetOrderList } from '@/api/order';
import { QUERY_ORDERS } from '@/constants';
import type { GetOrderResponse } from '@/generated';
import { ProcessedOrderSortableColumns } from '@/models/creditNoteSorting';
import { queryClient } from '@/plugins/queryClient';
import { type SortState } from '@/utils/localStorageUtil';
import { defineStore } from 'pinia';
import type { DataTableSortEvent } from 'primevue';
import { computed, ref, watch } from 'vue';

type OrdersProcessedSortState = SortState<ProcessedOrderSortableColumns>;

const DEFAULT_ORDERS_PROCESSED_SORT_STATE: OrdersProcessedSortState = {
    columnName: ProcessedOrderSortableColumns.ChangedAt,
    columnOrder: -1,
};

/** This store enables only navigation through the order list and its single elements managed
 * by TSQ in orders.ts */
const useOrderStore = defineStore(
    'order',
    () => {
        const { data: orders, isSuccess } = useGetOrderList();
        const orderIndex = ref<number>(0); // Index of the current order, for direct iterations back and forth
        const orderTotalCount = ref<number>(0); // total # of orders
        const hasProcessedOrders = ref<boolean>(false);
        const hasSpecialCasesOrders = ref<boolean>(false);
        const endOfList = ref<boolean>(false); // whether the end of the orders list has been reached, used to disable the "next" button in the UI
        const currentOrder = ref<GetOrderResponse['order'] | undefined>(
            orders.value?.at(orderIndex.value || 0)?.order
        );

        const resetCurrentOrder = () => {
            if (!orders.value) {
                return;
            }

            if (orders.value.length >= 1) {
                const nextIndex =
                    orderIndex.value >= orders.value.length
                        ? orders.value.length - 1
                        : orderIndex.value;
                currentOrder.value = orders.value?.at(nextIndex)?.order;
                endOfList.value = false;
            } else {
                currentOrder.value = undefined;
                endOfList.value = true;
            }
        };

        const goToFirstOrder = async () => {
            await queryClient.invalidateQueries({ queryKey: [QUERY_ORDERS] });
            if (!orders.value) {
                return;
            }

            orderIndex.value = orders.value.length >= 1 ? 0 : -1;
            resetCurrentOrder();
        };

        const previous = async () => {
            if (orderIndex.value - 1 < 0) {
                orderIndex.value = 0;
            } else {
                orderIndex.value = orderIndex.value - 1;
            }
            currentOrder.value = orders.value?.at(orderIndex.value)?.order;
        };

        const next = async () => {
            if (orderIndex.value + 1 >= (orders.value?.length || 0)) {
                orderIndex.value = (orders.value?.length || 0) - 1;
            } else {
                orderIndex.value = orderIndex.value + 1;
            }
            currentOrder.value = orders.value?.at(orderIndex.value)?.order;
        };

        const contains = (id: number): boolean => !!orders.value?.find(o => o?.order?.id === id);

        const tableSortChange = (event: DataTableSortEvent): void => {
            ordersProcessedSortState.value.columnName =
                (event.sortField as ProcessedOrderSortableColumns) ?? null;
            ordersProcessedSortState.value.columnOrder = event.sortOrder === 1 ? 1 : -1;
        };

        const ordersProcessedSortState = ref<OrdersProcessedSortState>({
            ...DEFAULT_ORDERS_PROCESSED_SORT_STATE,
        });

        // Computed property for sort order (true = ascending, false = descending)
        const isDescending = computed({
            get: () => ordersProcessedSortState.value.columnOrder > 0,
            set: (value: boolean) => {
                ordersProcessedSortState.value = {
                    ...ordersProcessedSortState.value,
                    columnOrder: value ? 1 : -1,
                };
            },
        });

        // Computed property for the selected column
        const selectedColumn = computed({
            get: () => ordersProcessedSortState.value.columnName,
            set: (value: ProcessedOrderSortableColumns) => {
                ordersProcessedSortState.value = {
                    ...ordersProcessedSortState.value,
                    columnName: value,
                };
            },
        });

        /** Watches for a new loaded order list, if there are orders, go to the first
         * one in there. */
        watch(
            isSuccess,
            () => {
                orderTotalCount.value = orders.value?.length || 0;
                goToFirstOrder();
            },
            {
                deep: true,
                immediate: true,
            }
        );

        watch(
            () => orders.value?.length,
            () => {
                orderTotalCount.value = orders.value?.length || 0;
            },
            {
                immediate: true,
            }
        );

        return {
            index: orderIndex,
            total: orderTotalCount,
            currentOrder,
            hasProcessedOrders,
            hasSpecialCasesOrders,
            endOfList,
            previous,
            next,
            contains,
            resetCurrentOrder,
            goToFirstOrder,
            tableSortChange,
            ordersProcessedSortState,
            isDescending,
            selectedColumn,
        };
    },
    {
        persist: [
            {
                storage: localStorage,
                pick: ['ordersProcessedSortState'],
                key: 'opss',
            },
            {
                storage: localStorage,
                pick: ['pageIndex', 'pageSize'],
                key: 'opis',
            },
        ],
    }
);

export default useOrderStore;
